/**
 * Player right-click (context) menu, themed to match the settings panel.
 *
 * Live-appropriate actions only — no Loop / "copy URL at current time" style
 * items that assume a fixed-length video. All labels render via textContent;
 * only static icon markup goes through innerHTML.
 */

import type Player from 'video.js/dist/types/player'

const SVG_ATTRS =
  'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"'

const ICON_LINK = `<svg ${SVG_ATTRS}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`
const ICON_CODE = `<svg ${SVG_ATTRS}><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`
const ICON_PIP = `<svg ${SVG_ATTRS}><rect x="2" y="4" width="20" height="16" rx="2"/><rect x="12" y="12" width="8" height="6" rx="1"/></svg>`
const ICON_BUG = `<svg ${SVG_ATTRS}><path d="M8 2v3M16 2v3M5 10h14M6 10v5a6 6 0 0 0 12 0v-5a6 6 0 0 0-12 0z"/><path d="M2 13h4M18 13h4M4 20l3-2M20 20l-3-2"/></svg>`
const ICON_HELP = `<svg ${SVG_ATTRS}><circle cx="12" cy="12" r="10"/><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`
const ICON_INFO = `<svg ${SVG_ATTRS}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>`
const ICON_PLAY = `<svg ${SVG_ATTRS}><polygon points="6 3 20 12 6 21 6 3" fill="currentColor"/></svg>`
const ICON_PAUSE =
  `<svg ${SVG_ATTRS}><rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor"/>` +
  `<rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor"/></svg>`
const ICON_SHARE = `<svg ${SVG_ATTRS}><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="10.5" x2="15.4" y2="6.5"/><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/></svg>`

const ICON_CHAT =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Zm0 14H5.2L4 17.2V4h16v12Z"/>' +
  '<path d="M6.5 7.5h11v1.6h-11zM6.5 11h7.5v1.6H6.5z"/></svg>'

export interface ContextMenuContext {
  embedId: string
  sessionId: string | null
  shareUrl: string
  /** Opens the share overlay (same one the settings menu uses) */
  onShare: () => void
  /** Opens the settings panel at its feedback view */
  onTroubleshoot: () => void
  /**
   * Live chat, when the embed has it. Read late, never captured: the panel is
   * built from a grant and can be created or disposed after this menu exists,
   * so the menu asks at the moment it renders instead of holding a reference.
   * Absent, or reporting unavailable, hides the entry entirely.
   */
  chat?: {
    isAvailable: () => boolean
    isOpen: () => boolean
    toggle: () => void
  }
}

export interface ContextMenuHandle {
  dispose(): void
}

interface MenuItem {
  /** May be a function so the entry can reflect live player state */
  label: string | (() => string)
  icon: string | (() => string)
  run: () => void | Promise<void>
  /** Rendered with a checkbox instead of running-and-closing */
  toggle?: () => boolean
  hidden?: () => boolean
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Clipboard API needs a secure context / permission — fall back
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      ta.remove()
      return ok
    } catch {
      return false
    }
  }
}

export function mountContextMenu(player: Player, ctx: ContextMenuContext): ContextMenuHandle {
  const playerEl = player.el() as HTMLElement

  const menu = document.createElement('div')
  menu.className = 'lp-context-menu'
  playerEl.appendChild(menu)

  const toast = document.createElement('div')
  toast.className = 'lp-context-toast'
  playerEl.appendChild(toast)

  const stats = document.createElement('div')
  stats.className = 'lp-stats-panel'
  playerEl.appendChild(stats)

  let open = false
  let statsOn = false
  let statsTimer: ReturnType<typeof setInterval> | undefined
  let toastTimer: ReturnType<typeof setTimeout> | undefined

  const flash = (message: string): void => {
    toast.textContent = message
    toast.classList.add('show')
    if (toastTimer) clearTimeout(toastTimer)
    toastTimer = setTimeout(() => toast.classList.remove('show'), 1800)
  }

  // ── stats for nerds ───────────────────────────────────────────────────

  interface VhsStats {
    bandwidth?: number
    buffered?: { start: number; end: number }[]
    videoBitrate?: number
  }

  const vhsStats = (): VhsStats | null => {
    try {
      const tech = (player as unknown as { tech: (safe: boolean) => { vhs?: { stats?: VhsStats } } }).tech(true)
      return tech?.vhs?.stats ?? null
    } catch {
      return null
    }
  }

  const readStats = (): [string, string][] => {
    const video = playerEl.querySelector('video') as HTMLVideoElement | null
    const rows: [string, string][] = []
    const s = vhsStats()

    rows.push(['Embed', ctx.embedId])
    if (video) {
      rows.push(['Resolution', `${video.videoWidth}×${video.videoHeight}`])
      const q = video.getVideoPlaybackQuality?.()
      if (q) rows.push(['Dropped frames', `${q.droppedVideoFrames} / ${q.totalVideoFrames}`])
      // Buffer ahead of the playhead
      try {
        const bufEnd = video.buffered.length ? video.buffered.end(video.buffered.length - 1) : 0
        rows.push(['Buffer health', `${Math.max(0, bufEnd - video.currentTime).toFixed(1)}s`])
      } catch {
        /* ignore */
      }
      rows.push(['Volume', `${Math.round((video.muted ? 0 : video.volume) * 100)}%`])
    }
    if (s?.bandwidth) rows.push(['Connection', `${Math.round(s.bandwidth / 1000)} kbps`])
    if (s?.videoBitrate) rows.push(['Stream bitrate', `${Math.round(s.videoBitrate / 1000)} kbps`])

    // Live latency: how far behind the live edge we are
    try {
      const seekable = player.seekable()
      if (seekable && seekable.length) {
        const behind = seekable.end(seekable.length - 1) - (player.currentTime() ?? 0)
        rows.push(['Live latency', `${Math.max(0, behind).toFixed(1)}s`])
      }
    } catch {
      /* ignore */
    }
    if (ctx.sessionId) rows.push(['Session', ctx.sessionId.slice(0, 12)])
    return rows
  }

  const renderStats = (): void => {
    stats.textContent = ''
    const title = document.createElement('div')
    title.className = 'lp-stats-title'
    title.textContent = 'Stats for nerds'
    stats.appendChild(title)
    for (const [k, v] of readStats()) {
      const row = document.createElement('div')
      row.className = 'lp-stats-row'
      const key = document.createElement('span')
      key.textContent = k
      const val = document.createElement('span')
      val.textContent = v
      row.append(key, val)
      stats.appendChild(row)
    }
  }

  const setStats = (on: boolean): void => {
    statsOn = on
    stats.classList.toggle('show', on)
    if (statsTimer) clearInterval(statsTimer)
    if (on) {
      renderStats()
      statsTimer = setInterval(renderStats, 1000)
    }
  }

  const debugInfo = (): string =>
    JSON.stringify(
      {
        embedId: ctx.embedId,
        sessionId: ctx.sessionId,
        url: ctx.shareUrl,
        source: player.currentSrc(),
        stats: Object.fromEntries(readStats()),
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      },
      null,
      2,
    )

  const embedCode = (): string =>
    `<iframe src="${ctx.shareUrl}" width="960" height="540" frameborder="0" ` +
    `allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`

  const pipSupported = (): boolean =>
    typeof document !== 'undefined' && 'pictureInPictureEnabled' in document &&
    (document as Document & { pictureInPictureEnabled: boolean }).pictureInPictureEnabled

  // ── items ─────────────────────────────────────────────────────────────

  const items: MenuItem[] = [
    {
      // Reflects the current state — and stays available while the control
      // bar is locked behind the pause overlay.
      label: () => (player.paused() ? 'Play' : 'Pause'),
      icon: () => (player.paused() ? ICON_PLAY : ICON_PAUSE),
      run: () => {
        if (player.paused()) void player.play()?.catch(() => undefined)
        else player.pause()
      },
    },
    {
      label: 'Copy video URL',
      icon: ICON_LINK,
      run: async () => flash((await copyText(ctx.shareUrl)) ? 'Link copied' : 'Copy failed'),
    },
    {
      label: 'Copy embed code',
      icon: ICON_CODE,
      run: async () => flash((await copyText(embedCode())) ? 'Embed code copied' : 'Copy failed'),
    },
    { label: 'Share', icon: ICON_SHARE, run: () => ctx.onShare() },
    {
      // Also the escape hatch when the control bar is unreachable: video.js
      // hides it while the player is starting, and that is exactly when a
      // viewer waiting on a buffering stream wants the picture back.
      label: () => (ctx.chat?.isOpen() ? 'Hide live chat' : 'Show live chat'),
      icon: ICON_CHAT,
      hidden: () => !ctx.chat?.isAvailable(),
      run: () => ctx.chat?.toggle(),
    },
    {
      label: 'Picture-in-picture',
      icon: ICON_PIP,
      hidden: () => !pipSupported(),
      run: async () => {
        try {
          const doc = document as Document & { pictureInPictureElement?: Element | null }
          if (doc.pictureInPictureElement) await (document as Document & { exitPictureInPicture: () => Promise<void> }).exitPictureInPicture()
          else await (playerEl.querySelector('video') as HTMLVideoElement & { requestPictureInPicture: () => Promise<unknown> }).requestPictureInPicture()
        } catch {
          flash('Picture-in-picture unavailable')
        }
      },
    },
    {
      label: 'Copy debug info',
      icon: ICON_BUG,
      run: async () => flash((await copyText(debugInfo())) ? 'Debug info copied' : 'Copy failed'),
    },
    { label: 'Troubleshoot playback issue', icon: ICON_HELP, run: () => ctx.onTroubleshoot() },
    { label: 'Stats for nerds', icon: ICON_INFO, run: () => setStats(!statsOn), toggle: () => statsOn },
  ]

  const render = (): void => {
    menu.textContent = ''
    for (const item of items) {
      if (item.hidden?.()) continue
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'lp-context-item'

      const icon = document.createElement('span')
      icon.className = 'lp-context-icon'
      icon.innerHTML = typeof item.icon === 'function' ? item.icon() : item.icon // static markup
      const label = document.createElement('span')
      label.className = 'lp-context-label'
      label.textContent = typeof item.label === 'function' ? item.label() : item.label
      btn.append(icon, label)

      if (item.toggle) {
        const box = document.createElement('span')
        box.className = 'lp-context-check' + (item.toggle() ? ' on' : '')
        btn.appendChild(box)
      }

      btn.addEventListener('click', (ev) => {
        ev.stopPropagation()
        void item.run()
        setOpen(false)
      })
      menu.appendChild(btn)
    }
  }

  const setOpen = (next: boolean, x = 0, y = 0): void => {
    open = next
    if (open) {
      render()
      // Position within the player, flipping near the edges
      menu.style.visibility = 'hidden'
      menu.classList.add('open')
      const rect = playerEl.getBoundingClientRect()
      const mw = menu.offsetWidth
      const mh = menu.offsetHeight
      const left = Math.min(Math.max(8, x - rect.left), Math.max(8, rect.width - mw - 8))
      const top = Math.min(Math.max(8, y - rect.top), Math.max(8, rect.height - mh - 8))
      menu.style.left = `${left}px`
      menu.style.top = `${top}px`
      menu.style.visibility = ''
    } else {
      menu.classList.remove('open')
    }
  }

  const onContextMenu = (ev: MouseEvent): void => {
    ev.preventDefault()
    setOpen(true, ev.clientX, ev.clientY)
  }
  playerEl.addEventListener('contextmenu', onContextMenu)

  const onDocClick = (ev: MouseEvent): void => {
    if (open && !menu.contains(ev.target as Node)) setOpen(false)
  }
  document.addEventListener('click', onDocClick, true)

  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape' && open) setOpen(false)
  }
  document.addEventListener('keydown', onKey, true)

  return {
    dispose: () => {
      if (statsTimer) clearInterval(statsTimer)
      if (toastTimer) clearTimeout(toastTimer)
      playerEl.removeEventListener('contextmenu', onContextMenu)
      document.removeEventListener('click', onDocClick, true)
      document.removeEventListener('keydown', onKey, true)
      menu.remove()
      toast.remove()
      stats.remove()
    },
  }
}
